import { sampleTerrainHeight } from "./terrain-data.js";

let terrainData = null;
const SYNTHETIC_BASE_DEPTH_M = 60;

export function setTerrainData(next) {
  terrainData = next && typeof next === "object" ? next : null;
}

export function clearTerrainData() {
  terrainData = null;
}

export function getTerrainData() {
  return terrainData;
}

export function sampleSeafloorHeightWithData(preset, _range, x, zWorld, data) {
  if (preset === "etopo_aoi") {
    const sampled = sampleTerrainHeight(data, x, zWorld);
    if (Number.isFinite(sampled)) {
      return sampled;
    }
  }
  return sampleSyntheticSeafloorHeight(preset, x, zWorld);
}

export function sampleSeafloorHeight(preset, range, x, zWorld) {
  return sampleSeafloorHeightWithData(preset, range, x, zWorld, terrainData);
}

export function sampleSeafloorHeightWithDataStrict(preset, _range, x, zWorld, data) {
  if (preset === "etopo_aoi") {
    const sampled = sampleTerrainHeight(data, x, zWorld);
    return Number.isFinite(sampled) ? sampled : null;
  }
  return sampleSyntheticSeafloorHeight(preset, x, zWorld);
}

function sampleSyntheticSeafloorHeight(preset, x, zWorld) {
  const y0 = -zWorld;
  let h = -SYNTHETIC_BASE_DEPTH_M;

  if (preset === "flat") {
    // True geometric plane for motion/stabilization teaching demos.
    return h;
  } else if (preset === "slope") {
    h -= x * 0.2;
  } else if (preset === "ridge") {
    h += Math.cos(x * 0.08) * 7 + Math.sin(y0 * 0.04) * 4.5;
  } else if (preset === "channel_3to1") {
    const absX = Math.abs(x);
    const channelDepth = 22;
    const bottomHalfWidth = 24;
    const sideSlopeHtoV = 3;
    const sideRun = channelDepth * sideSlopeHtoV;
    const rimX = bottomHalfWidth + sideRun;
    let offset = 0;
    if (absX <= bottomHalfWidth) {
      offset = channelDepth;
    } else if (absX < rimX) {
      offset = Math.max(0, channelDepth - ((absX - bottomHalfWidth) / sideSlopeHtoV));
    }
    h -= offset;
    // Keep relief smooth and subtle so the channel doesn't look faceted or blocky.
    h += Math.sin(x * 0.045 + y0 * 0.01) * 0.9;
    h += Math.cos(x * 0.12 - y0 * 0.03) * 0.35;
    h += Math.sin((x + y0) * 0.018) * 0.45;
    return h;
  } else if (preset === "shipwreck_titanic") {
    const cx = 260;
    const cz = 0;
    const headingRad = (18 * Math.PI) / 180;
    const cosH = Math.cos(headingRad);
    const sinH = Math.sin(headingRad);
    const dx = x - cx;
    const dz = zWorld - cz;
    const radialSq = dx * dx + dz * dz;
    if (radialSq > (430 * 430)) {
      h += Math.sin(x * 0.048 + y0 * 0.014) * 0.65 + Math.cos(x * 0.082 - y0 * 0.019) * 0.42;
      return h;
    }
    const lx = dx * cosH - dz * sinH;
    const lz = dx * sinH + dz * cosH;

    const bowCore = Math.max(0, 1 - (((Math.abs(lx - 86) / 78) ** 2.25) + ((Math.abs(lz) / 14.5) ** 3.1)));
    const sternCore = Math.max(0, 1 - (((Math.abs(lx + 74) / 54) ** 2.2) + ((Math.abs(lz) / 16.8) ** 2.9)));
    const bowHull = (bowCore ** 1.2) * 9.2;
    const sternHull = (sternCore ** 1.25) * 7.5;

    const bowDeck = Math.max(0, 1 - (((Math.abs(lx - 88) / 66) ** 2.4) + ((Math.abs(lz) / 6.2) ** 3.8))) * 3.3;
    const sternDeck = Math.max(0, 1 - (((Math.abs(lx + 72) / 42) ** 2.3) + ((Math.abs(lz) / 7.3) ** 3.5))) * 2.7;
    const stem = gaussian2(lx - 158, lz, 11, 4.2, 1.9) * 2.6;

    const gapMain = gaussian2(lx + 2, lz, 19, 10.2, 1.6) * 6.6;
    const gapShear = gaussian2(lx - 11, lz + 1.7, 13.5, 7.2, 1.75) * 2.8;
    const centerTrench = gaussian2(lx + 6, lz, 112, 16, 1.08) * 1.3;

    const sideScourPort = gaussian2(lx - 10, lz + 21, 118, 12, 1.25) * 1.2;
    const sideScourStbd = gaussian2(lx - 8, lz - 22, 109, 12, 1.25) * 1.05;

    const debrisA = gaussian2(lx - 20, lz + 31, 12, 8, 1.85) * 1.9;
    const debrisB = gaussian2(lx - 36, lz - 27, 11, 7.5, 1.9) * 1.55;
    const debrisC = gaussian2(lx + 28, lz + 34, 10.5, 7, 1.95) * 1.45;
    const debrisD = gaussian2(lx + 56, lz - 30, 10, 6.4, 2.0) * 1.25;
    const debrisE = gaussian2(lx + 2, lz - 41, 12, 7, 1.9) * 1.15;
    const debrisF = gaussian2(lx - 65, lz + 19, 13, 8, 1.8) * 1.35;
    const debrisG = gaussian2(lx + 84, lz + 18, 9, 6, 2.0) * 0.95;
    const debrisH = gaussian2(lx - 96, lz - 16, 11, 7, 1.85) * 1.05;

    const wreckRelief = (
      bowHull + sternHull + bowDeck + sternDeck + stem
      - gapMain - gapShear - centerTrench - sideScourPort - sideScourStbd
      + debrisA + debrisB + debrisC + debrisD + debrisE + debrisF + debrisG + debrisH
    );
    h += wreckRelief;

    const localRough = (
      Math.sin((lx * 0.21) + (lz * 0.33)) * 0.22
      + Math.sin((lx * 0.46) - (lz * 0.17)) * 0.16
      + Math.cos((lx * 0.68) + (lz * 0.61)) * 0.11
    );
    const roughWeight = Math.max(0, 1 - (Math.abs(lx) / 220)) * Math.max(0, 1 - (Math.abs(lz) / 95));
    h += localRough * roughWeight;

    h += Math.sin(x * 0.05 + y0 * 0.015) * 0.7 + Math.cos(x * 0.09 - y0 * 0.02) * 0.45;
    return h;
  }

  h += Math.sin(x * 0.06) * 1.2 + Math.cos(y0 * 0.05) * 1.4;
  return h;
}

function gaussian2(x, z, sx, sz, k = 1) {
  return Math.exp(-((((x / sx) ** 2) + ((z / sz) ** 2)) * k));
}
